import React from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { noop } from 'lodash/fp';
import { FormCard } from '@qbe/ads-blocks-forms';
import { useContent, useContentContext } from '@qbe/dfc-content';
import { TextField, Button, Dropdown, FormGroupLabel, DatePicker } from '@qbe/ads-core';
import { InformationPopUp, FormSpacer } from 'components';
import { FormDataType, genderList, titleList, IndOrBus, Title, Gender } from 'contexts/FormProvider';
import ContactOptions from '../components/ContactOptions';
import { emailSchema } from '../schema';
import { Dayjs } from 'dayjs';
import { setFormCompleteEvent, setFormStartEvent } from 'hooks/useAnalytics';
import { Moment } from 'moment';
import { createFormErrorMessage } from 'utils/helperMethods';

interface Props {
  indOrBus: IndOrBus;
  show: boolean;
  form: UseFormReturn<FormDataType>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formRef?: any;
  showButton: boolean;
  enableButton: boolean;
  onSubmit: () => void;
}

const PersonalDetails = (props: Props) => {
  const { indOrBus, show, form, showButton = true, enableButton = false, onSubmit = noop } = props;
  const {
    control,
    formState: { errors },
    watch,
    setValue,
    setError,
    clearErrors: clearError,
    trigger: triggerValidation,
    register,
  } = form;

  const { getTextContent } = useContent({
    context: useContentContext(),
  });

  const customerType = indOrBus || '';
  const isNotBusiness = customerType !== 'business';

  const cEmailAddress = watch('cEmailAddress') || '';
  const isPhysAddressValid = watch('isPhysAddressValid');
  const isManualPhysAddress = watch('isManualPhysAddress');
  const isManualPhysAddressValid = watch('isManualPhysAddressValid');

  const isComplaint = watch('lodgement') === 'Complaint';

  React.useEffect(() => {
    register('title');
    register('firstname');
    register('lastname');
    register('dob');
    register('gender');
  }, [register]);

  React.useEffect(() => {
    if (!isComplaint) {
      register('cEmailAddress');
      register('physicalAddress');
      register('physicalAddressObject');
      register('phyStreetAddress');
      register('phySuburb');
      register('phyCountry');
      register('phyState');
      register('phyPostcode');
      register('isPhysAddressValid');
      register('isManualPhysAddress');
      register('isManualPhysAddressValid');
      register('mPhoneNumber');
      register('hPhoneNumber');
      register('oPhoneNumber');
      register('hasOneValidField' as keyof FormDataType);
    }
  }, [register, isComplaint]);

  const isPhysicalAddressValid = React.useMemo(
    () => (!isManualPhysAddress ? isPhysAddressValid : isManualPhysAddressValid),
    [isManualPhysAddress, isPhysAddressValid, isManualPhysAddressValid]
  );

  React.useEffect(() => {
    if (!isComplaint) {
      const isEmailValid = emailSchema({ excludeEmptyString: false, required: false }).isValidSync(cEmailAddress);

      const hasOneValidField = [isEmailValid, isPhysicalAddressValid].some((isValid) => isValid);

      setValue('hasOneValidField' as keyof FormDataType, hasOneValidField);

      if (!isEmailValid || !isPhysicalAddressValid) {
        setError(
          'hasOneValidField' as keyof FormDataType,
          createFormErrorMessage('required', 'must have one valid field')
        );
      } else clearError('hasOneValidField' as keyof FormDataType);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComplaint, cEmailAddress, isPhysicalAddressValid]);

  if (!show) return null;

  function handleClick(): void {
    setFormCompleteEvent();
    onSubmit();
  }
  // Localized content constants
  const sectionTitle = getTextContent('yourDetails.personalDetails.section');
  const titleLabelTitle = getTextContent(
    `insured.customerDetails.${isNotBusiness ? 'customerTitle' : 'contactPersonTitle'}`
  );
  const nameLabelTitle = getTextContent(
    `insured.customerDetails.${isNotBusiness ? 'customerName' : 'contactPersonName'}`
  );
  const customerTitleLabel = getTextContent('insured.customerDetails.customerTitle.label');
  const firstNameLabel = getTextContent(
    `insured.customerDetails.${isNotBusiness ? 'customerName' : 'contactPersonName'}.firstName.label`
  );
  const lastNameLabel = getTextContent(
    `insured.customerDetails.${isNotBusiness ? 'customerName' : 'contactPersonName'}.lastName.label`
  );
  const dobTitle = getTextContent('insured.customerDetails.dob');
  const dobInfoText = getTextContent('insured.customerDetails.dob.infoText');
  const genderTitle = getTextContent('insured.customerDetails.gender');
  const genderLabel = getTextContent('insured.customerDetails.gender');
  const submitOrNextLabel = getTextContent(`insured.customerDetails.${!isComplaint ? 'submit' : 'next'}.label`);

  return (
    <>
      <FormCard title={sectionTitle} variant="sectionTitle">
        <FormGroupLabel title={titleLabelTitle} alignCenter />
        <FormSpacer type="input" />
        <Controller
          control={control}
          name="title"
          defaultValue={'' as Title}
          render={({ field }) => (
            <Dropdown
              {...field}
              sx={{ maxWidth: '400px' }}
              id="title"
              label={customerTitleLabel}
              multiple={false}
              value={field.value ? field.value : ('' as Title)}
              optionList={titleList.map((t) => ({ label: t, value: t }))}
              data-track="title"
              data-track-value="capture"
              onChange={(event) => {
                const value = event.target.value as Title;
                field.onChange(value);
                setFormStartEvent();
              }}
            />
          )}
        />

        <FormGroupLabel title={nameLabelTitle} required alignCenter />
        <FormSpacer type="input" />
        <Controller
          name="firstname"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              id="firstname"
              error={!!errors.firstname}
              value={field.value}
              label={firstNameLabel}
              inputProps={{
                maxLength: 50,
                minLength: 1,
              }}
              data-track="firstname"
              data-track-value="mask"
              data-test="firstname"
              helperText={errors.firstname ? errors.firstname?.message : null}
              size="medium"
            />
          )}
        />

        <FormSpacer type="input" />

        <Controller
          name="lastname"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value}
              error={!!errors.lastname}
              label={lastNameLabel}
              id="lastname"
              data-track="lastname"
              data-track-value="mask"
              inputProps={{
                maxLength: 50,
                minLength: 1,
              }}
              data-test="lastname"
              helperText={errors.lastname ? errors.lastname?.message : null}
            />
          )}
        />

        {isComplaint && isNotBusiness && (
          <>
            <FormGroupLabel
              title={dobTitle}
              titleSuffix={<InformationPopUp message={dobInfoText} />}
              required
              alignCenter
            />
            <FormSpacer type="input" />
            <DatePicker
              sx={{ maxWidth: '400px' }} // ADS is currently 320. should be update to 400
              id="dob"
              name="dob"
              label="DD/MM/YYYY"
              format="DD/MM/YYYY"
              disableFuture
              error={!!errors.dob}
              onChange={(value: Dayjs) => {
                setValue('dob', value as Date | Moment | null, { shouldValidate: true });
              }}
              onClose={() => {
                triggerValidation('dob');
              }}
              slotProps={{
                textField: {
                  helperText: errors?.dob?.message?.toString(),
                  name: 'dob',
                  error: !!errors.dob,
                  onBlur: () => {
                    triggerValidation('dob');
                  },
                },
              }}
            />

            <FormGroupLabel title={genderTitle} required alignCenter />
            <FormSpacer type="input" />
            <Controller
              control={control}
              name="gender"
              defaultValue={'' as Gender}
              render={({ field }) => (
                <Dropdown
                  {...field}
                  id="gender"
                  multiple={false}
                  value={field.value ? field.value : ('' as Gender)}
                  sx={{ maxWidth: '400px' }}
                  label={genderLabel}
                  error={!!errors.gender}
                  optionList={genderList.map((g) => ({ label: g, value: g }))}
                  data-track="gender"
                  data-track-value="mask"
                />
              )}
            />
          </>
        )}

        {!isComplaint && (
          <>
            <input style={{ display: 'none' }} type="text" {...register('hasOneValidField' as keyof FormDataType)} />
            <ContactOptions form={form} />
          </>
        )}

        {showButton && (
          <>
            <FormSpacer type="formgroup" />
            <Button
              label={submitOrNextLabel}
              type={!isComplaint ? 'submit' : 'button'}
              disabled={!enableButton}
              onClick={isComplaint ? handleClick : undefined}
            />
          </>
        )}
      </FormCard>
    </>
  );
};

export default PersonalDetails;
